export interface FileValidationResult {
  valid: boolean
  error?: string
  errorParams?: Record<string, string>
}

export interface FileValidationOptions {
  maxSizeBytes: number
  allowedMimeTypes: string[]
}

const formatMaxSizeMb = (maxSizeBytes: number) => `${maxSizeBytes / (1024 * 1024)}MB`

export const validateFileUrl = async (
  url: string,
  options: FileValidationOptions
): Promise<FileValidationResult> => {
  if (!url) return { valid: true }

  const {
    maxSizeBytes,
    allowedMimeTypes,
  } = options

  try {
    const response = await fetch(url, {
      method: 'HEAD',
    })

    if (!response.ok) {
      return { valid: false, error: 'URL is inaccessible' }
    }

    const contentType = response.headers.get('content-type')
    if (
      !contentType ||
      !allowedMimeTypes.some(type => contentType.includes(type))
    ) {
      return {
        valid: false,
        error: `URL does not point to a supported file type`,
      }
    }

    const contentLength = response.headers.get('content-length')
    if (!contentLength) {
      return {
        valid: false,
        error: 'Unable to determine file size',
      }
    }

    const size = Number(contentLength)
    if (!Number.isFinite(size)) {
      return {
        valid: false,
        error: 'Invalid file size',
      }
    }

    if (size > maxSizeBytes) {
      return {
        valid: false,
        error: 'File/s exceeds the maximum size of %{maxSize}',
        errorParams: { maxSize: formatMaxSizeMb(maxSizeBytes) },
      }
    }

    return { valid: true }
  } catch (error) {
    if (error instanceof TypeError) {
      if (url.toLowerCase().includes('.pdf')) {
        return { valid: true }
      }
      return { valid: false, error: 'Network or CORS error' }
    }

    return {
      valid: false,
      error: 'Unable to validate URL. Please check that it is accessible and points to a valid file',
    }
  }
}
